<template>
    <ModalFormCard :action="info.action" :submittedCallback="submittedCallback" @onError="onError" class="modal-default wide">
        <template #title>{{ isEdit ? 'Edit' : 'Create' }} {{ info.title }}</template>

        <template #header-buttons-left>
            <button
                v-if="options?.lead?.getCall() != null && options?.lead?.getCall().recording_url != null"
                @click="showAudio = !showAudio"
                type="button"
                class="w-7 h-7 flex items-center justify-center"
            >
                <IconVoiceSearch class="w-4.5 h-4.5 hover:opacity-80" :class="showAudio ? 'stroke-white' : 'stroke-grey-900'" />
            </button>
        </template>

        <template #default="{ saving, errors }">
            <input :value="value.origin_id" name="origin_id" type="hidden" />
            <!-- <input type='hidden' name='origin' :value='value.origin' /> -->

            <div
                v-if="showAudio"
                :class="
                    options.lead.getCall() &&
                    (options.lead.getCall().call_status != 'no-answer' || options.lead.getCall().recording_url != null)
                        ? 'pb-6'
                        : ''
                "
            >
                <Call :call="options.lead.getCall()" :showTitle="false" :showDescription="false" />
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                <Input
                    v-model:value="value.first_name"
                    :ai="ai"
                    groupClass="bg-grey text-15"
                    label="First name"
                    name="first_name"
                    placeholder="First name"
                />

                <Input
                    v-model:value="value.last_name"
                    :ai="ai"
                    groupClass="bg-grey text-15"
                    label="Last name"
                    name="last_name"
                    placeholder="Last name"
                />
            </div>

            <Input
                v-if="!isEdit || info.title == 'lead'"
                v-model:value="value.email"
                :ai="ai"
                class="mt-4"
                groupClass="bg-grey text-15"
                label="Email address"
                name="email"
                placeholder="Email address"
            />

            <Input
                v-if="!isEdit || info.title == 'lead'"
                v-model:value="value.phone_number"
                :ai="ai"
                class="mt-4"
                groupClass="bg-grey text-15"
                label="Phone number"
                name="phone_number"
                placeholder="Phone number"
            />

            <Input
                v-model:value="value.address_line_1"
                :ai="ai"
                class="mt-4"
                groupClass="bg-grey text-15"
                label="Address"
                name="address_line_1"
                placeholder="Line 1"
            />
            <Input
                v-model:value="value.address_line_2"
                :ai="ai"
                class="mt-1"
                groupClass="bg-grey text-15"
                name="address_line_2"
                placeholder="Line 2"
            />
            <Input v-model:value="value.town" :ai="ai" class="mt-1" groupClass="bg-grey text-15" name="town" placeholder="City / town" />
            <Input
                v-model:value="value.postcode"
                :ai="ai"
                class="mt-1"
                groupClass="bg-grey text-15"
                name="postcode"
                placeholder="Post code"
            />

            <template v-if="info.title == 'lead'">
                <Select
                    v-model:value="value.title"
                    class="mt-4"
                    :options="categoriesOptions"
                    label="Lead category"
                    name="title"
                    placeholder="Lead category"
                    selectClass="!bg-grey text-15"
                />

                <Textarea
                    v-model:value="value.description"
                    rows="3"
                    class="mt-4"
                    groupClass="bg-grey text-15"
                    label="Lead description"
                    name="description"
                    placeholder="Lead description"
                />
            </template>

            <Select
                v-if="value.origin_id == null"
                v-model:value="value.source_id"
                :options="sources"
                placeholder="Source"
                class="mt-4"
                label="Source"
                menuClass="!bg-grey"
                name="source_id"
                selectClass="!bg-grey text-15"
                forceDirection="bottom"
            >
                <template #label-right>
                    <NuxtLink :to="user.crmLink()+'/lead_source'" @click="$emit('close')" class="link-text text-purple-600 text-14"
                        >Manage sources</NuxtLink
                    >
                </template>
            </Select>

            <Select
                v-if="isEditing && info.title == 'prospect'"
                v-model:value="value.status"
                :options="[
                    {
                        value: 'Active',
                        html: `<div class=&quot;flex items-center&quot;><div class=&quot;w-2 h-2 rounded-full bg-green-600&quot;></div><p class=&quot;pl-2&quot;>Active</p></div>`,
                    },
                    {
                        value: 'Archived',
                        html: `<div class=&quot;flex items-center&quot;><div class=&quot;w-2 h-2 rounded-full bg-grey-900&quot;></div><p class=&quot;pl-2&quot;>Archived</p></div>`,
                    },
                ]"
                class="mt-4"
                label="Status"
                menuClass="!bg-grey"
                name="status"
                selectClass="!bg-grey text-15"
            />

            <div class="modal-buttons">
                <ButtonSaving :disabled="saving" class="button-black default" name="published_at" value="1">
                    {{ isEditing ? 'Update' : 'Create' }} {{ info.title }}
                </ButtonSaving>

                <button v-if="!isEdit" class="h-12 button-purple" name="redirect_after" value="1">
                    {{ isEditing ? 'Update' : 'Create' }} and view
                </button>

                <button v-else class="h-12 button-purple" v-if="info.title == 'prospect' || (info.title == 'lead' && editingModel.created_by_user_id != null)" @click="$pop(info.delete, { editingModel: editingModel })">
                    Delete {{ info.title }}
                </button>
            </div>
        </template>
    </ModalFormCard>
</template>

<script>
import Contact from '~/models/contact.js'
import Lead from '~/models/lead.js'
import Modal from '~/mixins/modal.js'

export default {
    mixins: [Modal],

    data() {
        return {
            showAudio: false,
        }
    },

    created() {
        this.setup(this.options?.isLeadCreation ? Lead : Contact)

        if (this.editingModel == null && this.options != null && !this.options.isLeadCreation) {
            this.value = Contact.valuesFromLead(this.options.lead, this)
        }

        if (this.options?.isLeadCreation) {
            this.value.phone_number = this.value.phone_number.length > 0 ? this.value.phone_number.replace('+44', '0') : ''
        }
    },

    methods: {
        submittedCallback(res, event) {
            this.redirectAfterSubmit(event, '/contacts/' + res.id)

            this.$event.$emit('modal-form-completed')
            this.$event.$emit('modal-close')
        },

        onError() {
            setTimeout(() => {
                document.getElementById('merge-lead').addEventListener('click', this.merge)
            }, this.$help.globalSubmitDelay() + 100)
        },  

        merge() {
            this.$axios.post('/actions/lead-add-to-existing-contact', {
                contact_id: document.getElementById('merge-lead').dataset.id,
                lead_id: this.options.lead.id,
                lead_type: 100
            }).then((res) => {
                this.$event.$emit('modal-form-completed')
                this.$event.$emit('modal-close')
            })
        }
    },

    computed: {
        info() {
            if (this.options?.isLeadCreation) {
                return {
                    title: 'lead',
                    action: '/lead' + (this.isEditing ? '/' + this.editingModel.id : ''),
                    delete: 'LeadDelete'
                }
            }

            return {
                title: 'prospect',
                action: '/contact' + (this.isEditing ? '/' + this.editingModel.id : ''),
                delete: 'ContactDelete'
            }
        },  

        isEditing() {
            return this.editingModel != null
        },

        ai() {
            return this.options?.lead?.call?.ai != null ? this.options?.lead?.call.ai.body : false
        },

        user() {
            return this.$authSanctum.getUser()
        },

        sources() {
            let s = this.user.lead_sources.map((s) => {
                return { value: s.id, html: s.name }
            })

            return s
        },

        deal_categories() {
            return this.$authSanctum.getUser().deal_categories
        },

        categoriesOptions() {
            return this.deal_categories.map((n) => {
                return {
                    value: n.name,
                    html: this.$help.capitalizeFirstLetter(n.name),
                }
            })
        },
    },
}
</script>
